import Tip from "../../components/Tip";
import Faqs from "./Faqs";
import SagCalculator from "./SagCalculator";
import styles from "./SuspensionFaq.module.css";

const SuspensionFaq = () => {
  return (
    <Faqs
      faqs={[
        {
          question: "How to set up your mtb suspension for the first time?",
          answer: (
            <>
              <p>
                Start with manufacturer-recommended settings for sag, rebound,
                compression and any other adjustments. Use a shock pump or
                exchange spring weights to reach the desired sag first. Test on
                familiar trails before fine-tuning adjustments further.
              </p>
              <Tip>Adjust one setting at a time to feel the changes made.</Tip>
            </>
          ),
        },
        {
          question: "How to measure and set sag?",
          answer: (
            <>
              <p>
                Wear full riding gear and stand in your normal riding position
                on an even ground. For forks, aim for 15–20% sag; for rear
                shocks, 25–30% is standard for measuring static sag. Use a zip
                tie or the built-in o-ring to mark travel while standing on
                level ground.
              </p>
              <br />
              <p>
                <strong>Calculate Fork sag</strong>:
              </p>
              <br />
              <SagCalculator viewMode="fork" />
              <br />
              <p>
                <strong>Calculate Shock sag</strong>:
              </p>
              <br />
              <SagCalculator viewMode="shock" />
              <br />
              <p>
                Adjust air pressure or spring weight to hit your target range.
              </p>
            </>
          ),
        },
        {
          question: "How to adjust rebound damping?",
          answer: (
            <>
              <p>
                Rebound controls how quickly your suspension returns after
                compressing; too fast and it feels bouncy and unpredictable, too
                slow and it packs down giving a harsh ride. You are searching
                for a compromise; a supple yet predictable fork. Start with both
                High and Low Speed Rebound at the manufacturers recommended
                settings and tweak based on feel.
              </p>
              <Tip>
                High and Low speed rebound need to work together for you to get
                the best from your set up.
              </Tip>
            </>
          ),
        },
        {
          question: "How to adjust compression damping?",
          answer: (
            <p>
              Compression is the resistance set towards your suspension
              compressing. Too little resistance will cause your fork to dive
              and too closed gives a harsh ride. You are searching for a
              compromise; a supportive yet supple fork. Start with both High and
              Low Speed Compression at the manufacturers recommended settings
              and tweak based on feel.
            </p>
          ),
        },
        {
          question:
            "How to differentiate and what is Low and High speed Compression?",
          answer: (
            <>
              <p>
                Low and High speed damping work together. As the suspension
                compresses, oil is forced through a central hole. Adjusting your
                Low Speed compression opens or reduces this hole, effecting the
                oil flow. Under High Speed hits, pressure builds. At a certain
                pressure, oil will be released through secondary holes in a shim
                stack. Adjustment of High speed compression changes at what
                pressure the oil is allowed through this secondary system. In
                general Low Speed Compression is from rider input and High Speed
                Compression comes from impacts on the track.
              </p>
              <p>
                <strong>Low-speed compression (LSC)</strong>: Controls how the
                suspension reacts to slow, gradual forces—like pedaling, body
                weight shifts, braking, or berms. It's about stability.
              </p>
              <ul className={styles.list}>
                <li>
                  <strong>More LSC</strong>: Better support during climbing,
                  less brake dive, firmer ride feel.
                </li>
                <li>
                  <strong>Less LSC</strong>: More comfort and traction over
                  small bumps, but less support under rider input.
                </li>
              </ul>
              <p>
                <strong>High-speed compression (HSC)</strong>: Controls how the
                suspension handles fast, sharp impacts—like rocks, roots, jumps,
                and drops. It's about impact absorption.
              </p>
              <ul className={styles.list}>
                <li>
                  <strong>More HSC</strong>: Reduces bottom-outs on big hits,
                  but can feel harsh.
                </li>
                <li>
                  <strong>Less HSC</strong>: Softer on impacts and more plush,
                  but risks bottoming out on big landings.
                </li>
              </ul>
            </>
          ),
        },
        {
          question:
            "How to differentiate and what is Low and High speed Rebound?",
          answer: (
            <>
              <p>
                Low and High speed damping work closely together. As the
                suspension returns, oil is forced through a central hole.
                Adjusting your Low Speed Rebound opens or reduces this hole,
                effecting the resistance in oil flow. As an example, When your
                wheels leave the ground after an aggressive impact, the
                suspension returns back fast. At a certain pressure, oil will be
                released through secondary holes in a shim stack. Adjustment of
                High Speed Rebound changes at what pressure the oil is released
                through this secondary system. In general, Low Speed Rebounds
                are from rider input and High Speed Rebounds come from the track
                itself.
              </p>
              <p>
                <strong>Low-speed rebound (LSR)</strong>: Controls how quickly
                the suspension extends after slow, moderate compression—like
                after a berm, a smooth landing, or weight shift. It's about
                maintaining traction and control.
              </p>
              <ul className={styles.list}>
                <li>
                  <strong>Slower LSR</strong>: More composed and stable feel,
                  reduces bouncing, better grip in corners.
                </li>
                <li>
                  <strong>Faster LSR</strong>: Quicker reset for repeated hits,
                  but can feel twitchy or unstable if too fast.
                </li>
              </ul>
              <p>
                <strong>High-speed rebound (HSR)</strong>: Regulates rebound
                speed after fast, forceful compressions—like big hits, drops, or
                multiple roots in quick succession. It's about control under
                rapid suspension movement.
              </p>
              <ul className={styles.list}>
                <li>
                  <strong>Slower HSR</strong>: Helps keep the bike planted and
                  controlled after harsh impacts.
                </li>
                <li>
                  <strong>Faster HSR</strong>: Suspension recovers quicker
                  between big hits, but too fast can cause the bike to feel
                  unpredictable or "kick" back.
                </li>
              </ul>
            </>
          ),
        },
        {
          question: "How to tell when your suspension needs servicing?",
          answer: (
            <p>
              Look for oil leakage, reduced small-bump sensitivity, clunking
              noises, or noticeable stiffness. Most forks/shocks need a lower
              leg or air can service every 50 hours of ride time, and a full
              rebuild at 100-200 hours.
            </p>
          ),
        },
        {
          question: "How to troubleshoot common mtb suspension problems?",
          answer: (
            <>
              <p>
                Harsh ride? Check if rebound is too fast, pressure is too high
                or compression is too closed.
              </p>
              <p>
                Bottoming out? Add compression, reset sag or increase volume
                spacers.
              </p>
              <p>
                Fork stuck down? You may have a negative air spring
                issue—release pressure and cycle the fork.
              </p>
              <p>Always eliminate the basics first.</p>
            </>
          ),
        },
      ]}
    />
  );
};

export default SuspensionFaq;
